import type React from 'react';
import { useEffect, useState } from 'react';
import { authenticatedFetch } from '../utils/api';

interface SponsoredResult {
  id: string;
  keywords: string[];
  title: string;
  description: string;
  url?: string;
  forceResult?: {
    index: string;
    resultId: string;
    resultTitle: string;
  };
  category: 'sponsored' | 'targeted' | 'priority';
  startDate: string;
  endDate: string;
  priority: number;
  active: boolean;
  createdAt: string;
}

interface SponsoredResultsProps {
  onAddResult: () => void;
  onEditResult: (result: SponsoredResult) => void;
}

type ViewMode = 'cards' | 'table';
type CategoryFilter = 'all' | 'sponsored' | 'targeted' | 'priority';
type StatusFilter = 'all' | 'active' | 'inactive';
type DescriptionFilter = 'all' | 'with-description' | 'without-description';

const SponsoredResults: React.FC<SponsoredResultsProps> = ({ onAddResult, onEditResult }) => {
  const [sponsoredResults, setSponsoredResults] = useState<SponsoredResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [descriptionFilter, setDescriptionFilter] = useState<DescriptionFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    loadSponsoredResults();
  }, []);

  const loadSponsoredResults = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/sponsored-results`);
      if (response.ok) {
        const results = await response.json();
        setSponsoredResults(results);
      }
    } catch (error) {
      console.error('Failed to load sponsored results:', error);
    } finally {
      setLoading(false);
    }
  };

  const editResult = (result: SponsoredResult) => {
    onEditResult(result);
  };

  const deleteResult = async (id: string) => {
    if (!confirm('Are you sure you want to delete this sponsored result?')) return;

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/sponsored-results/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        loadSponsoredResults();
      }
    } catch (error) {
      console.error('Failed to delete sponsored result:', error);
    }
  };

  // Bulk delete selected results
  const deleteSelectedResults = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedIds.size} selected result(s)?`)) return;

    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedIds) {
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/sponsored-results/${id}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error('Failed to delete sponsored result:', id, error);
        errorCount++;
      }
    }

    setIsDeleting(false);
    setSelectedIds(new Set());
    loadSponsoredResults();

    if (errorCount > 0) {
      alert(`Deleted ${successCount} result(s). ${errorCount} failed.`);
    }
  };

  // Toggle selection for a single item
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Select/deselect all filtered results
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredResults.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredResults.map((r) => r.id)));
    }
  };

  // Helper function to search for a document by URL
  const searchByUrl = async (url: string): Promise<{ index: string; resultId: string; resultTitle: string } | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/search-by-url?url=${encodeURIComponent(url)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.hits?.hits?.length > 0) {
          const hit = data.hits.hits[0];
          return {
            index: hit._index,
            resultId: hit._id,
            resultTitle: hit._source.title || hit._source.name || 'Untitled',
          };
        }
      }
    } catch (error) {
      console.warn('Failed to search by URL:', url, error);
    }
    return null;
  };

  // Properly parse CSV line handling quoted fields with commas
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        // Check for escaped quote ("")
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    // Don't forget the last field
    result.push(current.trim());

    return result;
  };

  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split('\n').filter((line) => line.trim());

    const importedResults: SponsoredResult[] = [];
    const priorityResultsNeedingMatch: { index: number; url: string }[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length >= 4) {
        // Parse forceResult if present (JSON string)
        let forceResult:
          | {
              index: string;
              resultId: string;
              resultTitle: string;
            }
          | undefined;
        try {
          if (values[9] && values[9] !== '') {
            forceResult = JSON.parse(values[9]);
          }
        } catch (_e) {
          console.warn('Failed to parse forceResult:', values[9]);
        }

        const category = (values[4] as 'sponsored' | 'targeted' | 'priority') || 'sponsored';
        const url = values[3] || undefined;

        // Track priority results that need URL matching
        if (category === 'priority' && !forceResult && url) {
          priorityResultsNeedingMatch.push({ index: importedResults.length, url });
        }

        importedResults.push({
          id: Date.now().toString() + i,
          keywords: values[0].split('|').map((k) => k.trim()),
          title: values[1],
          description: values[2],
          url,
          category,
          startDate: values[5] || new Date().toISOString().split('T')[0],
          endDate: values[6] || '',
          priority: parseInt(values[7], 10) || 1,
          active: values[8] !== 'false',
          forceResult,
          createdAt: values[10] || new Date().toISOString(),
        });
      }
    }

    // Auto-match URLs for priority results that don't have forceResult
    const unmatchedUrls: string[] = [];
    if (priorityResultsNeedingMatch.length > 0) {
      for (const { index, url } of priorityResultsNeedingMatch) {
        try {
          const match = await searchByUrl(url);
          if (match) {
            importedResults[index].forceResult = match;
          } else {
            unmatchedUrls.push(url);
          }
        } catch (error) {
          unmatchedUrls.push(url);
        }
      }
    }

    // Load existing results to check for duplicates
    let existingResults: SponsoredResult[] = [];
    try {
      const existingResponse = await fetch(`${API_BASE_URL}/api/sponsored-results`);
      if (existingResponse.ok) {
        existingResults = await existingResponse.json();
      }
    } catch (error) {
      console.warn('Could not load existing results for duplicate check:', error);
    }

    // Create a map of existing results by title for quick lookup
    const existingByTitle = new Map<string, SponsoredResult>();
    for (const existing of existingResults) {
      existingByTitle.set(existing.title.toLowerCase(), existing);
    }

    // Import all results (update if exists, create if new)
    let createdCount = 0;
    let updatedCount = 0;
    let failedCount = 0;

    for (const result of importedResults) {
      try {
        // Check if result with same title already exists
        const existing = existingByTitle.get(result.title.toLowerCase());

        if (existing) {
          // Update existing result
          const response = await authenticatedFetch(`${API_BASE_URL}/api/sponsored-results`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...result, id: existing.id }),
          });
          if (response.ok) {
            updatedCount++;
          } else {
            const errorText = await response.text();
            console.error(`Failed to update "${result.title}":`, response.status, errorText);
            failedCount++;
          }
        } else {
          // Create new result
          const response = await authenticatedFetch(`${API_BASE_URL}/api/sponsored-results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(result),
          });
          if (response.ok) {
            createdCount++;
          } else {
            const errorText = await response.text();
            console.error(`Failed to create "${result.title}":`, response.status, errorText);
            failedCount++;
          }
        }
      } catch (error) {
        console.error('Failed to import result:', result.title, error);
        failedCount++;
      }
    }

    let message = `Import complete!\n\nCreated: ${createdCount}\nUpdated: ${updatedCount}\nFailed: ${failedCount}`;
    if (unmatchedUrls.length > 0) {
      message += `\n\nNote: ${unmatchedUrls.length} priority result(s) could not be auto-matched to documents. Edit them manually to set Force Result.`;
    }
    alert(message);

    loadSponsoredResults();
    e.target.value = '';
  };

  const exportToCSV = () => {
    const headers = [
      'Keywords',
      'Title',
      'Description',
      'URL',
      'Category',
      'Start Date',
      'End Date',
      'Priority',
      'Active',
      'Force Result',
      'Created At',
    ];
    const csvContent = [
      headers.join(','),
      ...sponsoredResults.map((result) =>
        [
          result.keywords.join('|'),
          `"${result.title}"`,
          `"${result.description}"`,
          result.url || '',
          result.category || 'sponsored',
          result.startDate || '',
          result.endDate || '',
          result.priority,
          result.active,
          result.forceResult ? `"${JSON.stringify(result.forceResult).replace(/"/g, '""')}"` : '',
          result.createdAt || '',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sponsored-results.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter results based on current filters
  const filteredResults = sponsoredResults.filter((result) => {
    // Category filter
    if (categoryFilter !== 'all' && result.category !== categoryFilter) {
      return false;
    }
    // Status filter
    if (statusFilter === 'active' && !result.active) {
      return false;
    }
    if (statusFilter === 'inactive' && result.active) {
      return false;
    }
    // Description filter
    if (descriptionFilter === 'with-description' && !result.description?.trim()) {
      return false;
    }
    if (descriptionFilter === 'without-description' && result.description?.trim()) {
      return false;
    }
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = result.title.toLowerCase().includes(query);
      const matchesKeywords = result.keywords.some((k) => k.toLowerCase().includes(query));
      const matchesDescription = result.description.toLowerCase().includes(query);
      if (!matchesTitle && !matchesKeywords && !matchesDescription) {
        return false;
      }
    }
    return true;
  });

  // Get category display name
  const getCategoryDisplayName = (category: string) => {
    if (category === 'targeted') return 'Promoted';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-zinc-200">
        <h2 className="text-2xl font-semibold text-zinc-900">Results Management</h2>
        <div className="flex gap-3 items-center">
          <input type="file" accept=".csv" onChange={handleCSVImport} style={{ display: 'none' }} id="csv-import" />
          <label
            htmlFor="csv-import"
            className="rounded-lg border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-medium cursor-pointer hover:bg-zinc-50 hover:border-zinc-400 transition-all"
          >
            Import CSV
          </label>
          <button
            onClick={exportToCSV}
            className="rounded-lg border border-zinc-300 text-zinc-700 px-4 py-2 text-sm font-medium hover:bg-zinc-50 hover:border-zinc-400 transition-all cursor-pointer"
          >
            Export CSV
          </button>
          <button
            onClick={onAddResult}
            className="rounded-lg bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 transition-all cursor-pointer"
          >
            Add Managed Result
          </button>
        </div>
      </div>

      {/* Important Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
        <h3 className="text-base font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          Important: Category Behavior & Keyword Matching
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <h4 className="font-semibold text-blue-800 mb-1">Sponsored & Promoted</h4>
            <p className="text-blue-700">
              Appear in the promoted section at the top. <strong>Maximum 3 combined</strong> per search. Sponsored shows a prominent blue badge; Promoted shows a subtle amber badge.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-green-800 mb-1">Priority</h4>
            <p className="text-green-700">
              Appear as the first regular results (no badge, no limit). Order controlled by priority number (1, 2, 3...). Requires selecting a Force Result.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-blue-800 mb-1">Exact Phrase Matching</h4>
            <p className="text-blue-700">
              Wrap keywords in quotes for exact phrase matching. Example:{' '}
              <code className="bg-blue-100 px-1 rounded">"failure analysis"</code> will only match that exact phrase.
            </p>
          </div>
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className="flex flex-wrap gap-4 mb-6 items-center justify-between bg-zinc-50 p-4 rounded-lg border border-zinc-200">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex items-center gap-2">
            <label htmlFor="search" className="text-sm font-medium text-zinc-700">
              Search:
            </label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Title, keywords, description..."
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent w-64"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="category-filter" className="text-sm font-medium text-zinc-700">
              Category:
            </label>
            <select
              id="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="all">All Categories</option>
              <option value="sponsored">Sponsored</option>
              <option value="targeted">Promoted</option>
              <option value="priority">Priority</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm font-medium text-zinc-700">
              Status:
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Description Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="description-filter" className="text-sm font-medium text-zinc-700">
              Description:
            </label>
            <select
              id="description-filter"
              value={descriptionFilter}
              onChange={(e) => setDescriptionFilter(e.target.value as DescriptionFilter)}
              className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="all">All</option>
              <option value="with-description">With Description</option>
              <option value="without-description">Without Description</option>
            </select>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-700">View:</span>
          <div className="flex rounded-lg border border-zinc-300 overflow-hidden">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
                viewMode === 'cards' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
                viewMode === 'table' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4 text-sm text-zinc-600">
        Showing {filteredResults.length} of {sponsoredResults.length} results
        {(categoryFilter !== 'all' || statusFilter !== 'all' || descriptionFilter !== 'all' || searchQuery) && (
          <button
            onClick={() => {
              setCategoryFilter('all');
              setStatusFilter('all');
              setDescriptionFilter('all');
              setSearchQuery('');
            }}
            className="ml-2 text-blue-600 hover:underline cursor-pointer"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-zinc-600">Loading managed results...</p>
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <p>{sponsoredResults.length === 0 ? 'No managed results configured yet.' : 'No results match the current filters.'}</p>
          </div>
        ) : viewMode === 'table' ? (
          /* Table View */
          <div className="overflow-x-auto border border-zinc-200 rounded-lg">
            {/* Bulk Actions Bar */}
            {selectedIds.size > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-blue-800 font-medium">
                  {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded transition-all cursor-pointer"
                  >
                    Clear Selection
                  </button>
                  <button
                    onClick={deleteSelectedResults}
                    disabled={isDeleting}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
                  </button>
                </div>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filteredResults.length > 0 && selectedIds.size === filteredResults.length}
                      onChange={toggleSelectAll}
                      className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      title="Select all"
                    />
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-700">Title</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-700">Category</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-700">Keywords</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-700">Priority</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-700">Date Range</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((result, idx) => (
                  <tr
                    key={result.id}
                    className={`border-b border-zinc-100 hover:bg-zinc-50 ${!result.active ? 'opacity-60 bg-zinc-50' : ''} ${idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'} ${selectedIds.has(result.id) ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(result.id)}
                        onChange={() => toggleSelection(result.id)}
                        className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-900 max-w-xs truncate" title={result.title}>
                        {result.title}
                      </div>
                      {result.forceResult?.resultTitle && (
                        <div className="text-xs text-green-700 mt-1">
                          Force: {result.forceResult.resultTitle.substring(0, 30)}...
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${
                          result.category === 'sponsored'
                            ? 'bg-blue-100 text-blue-800'
                            : result.category === 'priority'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {getCategoryDisplayName(result.category)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {result.keywords.slice(0, 3).map((keyword, idx) => (
                          <span key={idx} className="bg-zinc-200 text-zinc-700 px-1.5 py-0.5 rounded text-xs">
                            {keyword}
                          </span>
                        ))}
                        {result.keywords.length > 3 && (
                          <span className="text-xs text-zinc-500">+{result.keywords.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{result.priority}</td>
                    <td className="px-4 py-3 text-zinc-600 text-xs">
                      {result.startDate || 'No start'}
                      <br />
                      {result.endDate || 'No end'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          result.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {result.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => editResult(result)}
                          className="px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-all cursor-pointer"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteResult(result.id)}
                          className="px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-all cursor-pointer"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* Card View */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {filteredResults.map((result) => (
              <div
                key={result.id}
                className={`rounded-lg border bg-white p-5 transition-all hover:border-zinc-300 ${
                  !result.active ? 'opacity-60 bg-zinc-50' : 'border-zinc-200'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-zinc-900 flex-1">{result.title}</h3>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide leading-none ${
                      result.category === 'sponsored'
                        ? 'bg-blue-100 text-blue-800'
                        : result.category === 'priority'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {result.category === 'targeted' ? 'PROMOTED' : result.category?.toUpperCase() || 'SPONSORED'}
                  </span>
                </div>
                <p className="text-zinc-600 mb-4 leading-relaxed">{result.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {result.keywords.map((keyword, idx) => (
                    <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                      {keyword}
                    </span>
                  ))}
                </div>
                {result.forceResult?.resultTitle && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm">
                    <strong className="text-green-800">Forced Result:</strong>{' '}
                    <span className="text-green-900">{result.forceResult.resultTitle}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-xs text-zinc-600 mb-4">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">Priority: {result.priority}</span>
                    {result.startDate && (
                      <span className="text-zinc-500">
                        {result.startDate}
                        {result.endDate ? ` - ${result.endDate}` : ' - No end'}
                      </span>
                    )}
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium uppercase tracking-wide ${
                      result.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {result.active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Action buttons at the bottom */}
                <div className="flex gap-2 pt-3 border-t border-zinc-200">
                  <button
                    onClick={() => editResult(result)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all cursor-pointer"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteResult(result.id)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all cursor-pointer"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsoredResults;
